import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";

const EASE = [0.83, 0, 0.17, 1];

// A cinematic "threshold" into Il Salotto Digitale.
// An editorial masthead settles like a magazine cover, an accent hairline
// draws across, then two ink panels part like library doors to reveal the room.
export const SalottoIntro = () => {
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem("salotto_intro_seen")) return;
    setShow(true);
    sessionStorage.setItem("salotto_intro_seen", "1");
    document.body.style.overflow = "hidden";
    const done = setTimeout(() => {
      document.body.style.overflow = "";
      setShow(false);
    }, 3200);
    return () => {
      document.body.style.overflow = "";
      clearTimeout(done);
    };
  }, []);

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          data-testid="salotto-intro"
          className="fixed inset-0 z-[100] overflow-hidden pointer-events-none"
          exit={{ opacity: 0 }}
        >
          {/* Left door */}
          <motion.div
            className="absolute inset-y-0 left-0 w-1/2 bg-ink"
            initial={{ x: 0 }}
            animate={{ x: "-100%" }}
            transition={{ duration: 1.15, ease: EASE, delay: 1.95 }}
          />
          {/* Right door */}
          <motion.div
            className="absolute inset-y-0 right-0 w-1/2 bg-ink"
            initial={{ x: 0 }}
            animate={{ x: "100%" }}
            transition={{ duration: 1.15, ease: EASE, delay: 1.95 }}
          />

          {/* Spine — the glowing seam between the doors */}
          <motion.div
            className="absolute inset-y-0 left-1/2 -translate-x-1/2 w-px bg-accent"
            initial={{ opacity: 0, scaleY: 0 }}
            animate={{ opacity: [0, 0.7, 0.7, 0], scaleY: [0, 1, 1, 1] }}
            transition={{ duration: 2.2, ease: "easeInOut", times: [0, 0.25, 0.85, 1] }}
            style={{ transformOrigin: "center" }}
          />

          {/* Masthead — the editorial cover */}
          <motion.div
            className="absolute inset-0 flex flex-col items-center justify-center text-center px-6 text-bg"
            initial={{ opacity: 0 }}
            animate={{ opacity: [0, 1, 1, 0] }}
            transition={{ duration: 2.0, ease: "easeInOut", times: [0, 0.22, 0.72, 1] }}
          >
            <motion.span
              className="text-[10px] uppercase tracking-[0.5em] text-bg/50"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease: EASE, delay: 0.15 }}
            >
              Est. Miami, Florida
            </motion.span>

            <motion.h1
              className="mt-6 font-serif font-light text-5xl sm:text-6xl lg:text-[6.5rem] leading-[0.95] tracking-tight"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.1, ease: EASE, delay: 0.3 }}
            >
              Il Salotto
              <br />
              Digitale
            </motion.h1>

            <motion.span
              className="mt-8 block h-px bg-accent"
              initial={{ width: 0 }}
              animate={{ width: 220 }}
              transition={{ duration: 1.0, ease: EASE, delay: 0.55 }}
            />

            <motion.span
              className="mt-6 text-[11px] uppercase tracking-[0.35em] text-bg/60"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.9, ease: "easeOut", delay: 0.8 }}
            >
              The Library of Estate Management
            </motion.span>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
